// The part of F4's desktop preload contract (apps/desktop/src/shared/bridge.ts, version 1) that
// the shared renderer uses. tests/web/workspace/desktop.test.ts checks that F4's full interface
// stays assignable to this one. No method returns or accepts a token; HTTP and SSE stay
// same-origin `fetch` (app://sidetone), which the main process forwards to the API.
import { createContext, useContext } from 'react';
import type { DesktopCaptureBridge } from '@bcio-voice/call-client/core';

export type DesktopAuthState =
  | { status: 'unconfigured'; reason: 'api_origin_not_configured' | 'pkce_endpoints_not_configured' }
  | { status: 'signed_out'; last_error: string | null }
  | { status: 'awaiting_browser'; started_at: string; expires_at: string }
  | { status: 'exchanging' }
  | { status: 'signed_in'; access_expires_at: string; persistence: 'os_protected' | 'session_only' };

export interface DesktopDialLink {
  number: string;
  e164: string | null;
  source: 'tel' | 'callto' | 'app_link';
  received_at: string;
}

export type DesktopNotificationKind = 'incoming_call' | 'missed_call' | 'voicemail' | 'callback_due' | 'upload_attention';
export type DesktopTrayState = 'idle' | 'ringing' | 'on_call' | 'muted' | 'offline';
export type DesktopMediaAccess = 'granted' | 'denied' | 'restricted' | 'not-determined' | 'unknown';
export type DesktopPowerEvent = { kind: 'suspend' | 'resume' | 'lock-screen' | 'unlock-screen'; at: string };

export interface DesktopBridge {
  readonly bridgeVersion: number;
  auth: {
    state(): Promise<DesktopAuthState>;
    signIn(): Promise<DesktopAuthState>;
    cancel(): Promise<DesktopAuthState>;
    signOut(): Promise<DesktopAuthState>;
    onChange(listener: (state: DesktopAuthState) => void): () => void;
  };
  notify(request: { kind: DesktopNotificationKind; ref: string; caller?: { display_name: string | null; e164: string | null } }): Promise<{ shown: boolean; reason: string; detail_included: boolean }>;
  setRinging(ringing: { call_id: string } | null): Promise<void>;
  setTrayState(state: DesktopTrayState): Promise<void>;
  setCallActivity(activity: { active_calls: number }): Promise<void>;
  showWindow(): Promise<void>;
  devices: {
    microphoneAccess(): Promise<DesktopMediaAccess>;
    askMicrophone(): Promise<DesktopMediaAccess>;
    onPermissionDenied(listener: (event: { permission: string; at: string }) => void): () => void;
  };
  settings: {
    get(): Promise<{ notification_detail: 'hidden' | 'show_caller' }>;
    set(patch: { notification_detail?: 'hidden' | 'show_caller' }): Promise<{ notification_detail: 'hidden' | 'show_caller' }>;
  };
  onDialLink(listener: (link: DesktopDialLink) => void): () => void;
  onNotificationClicked(listener: (event: { kind: DesktopNotificationKind; ref: string }) => void): () => void;
  onPower(listener: (event: DesktopPowerEvent) => void): () => void;
  onTrayCommand(listener: (event: { command: 'mute_toggle' | 'pause_uploads_toggle' }) => void): () => void;
  /** Local capture spool (F4). Optional here: the shell works without it; see call/desktopCapture.ts. */
  capture?: DesktopCaptureBridge;
}

export const DESKTOP_BRIDGE_VERSION = 1;

interface Scope {
  location?: { protocol: string; host: string };
  sidetoneDesktop?: unknown;
}

/**
 * The genuine preload bridge: version 1, every method we call present, and only on the packaged
 * `app://sidetone` origin. Anything else is the ordinary web path.
 */
export function detectDesktopBridge(scope: Scope = globalThis as unknown as Scope): DesktopBridge | null {
  const loc = scope.location;
  if (!loc || loc.protocol !== 'app:' || loc.host !== 'sidetone') return null;
  const b = scope.sidetoneDesktop as Partial<DesktopBridge> | null | undefined;
  if (!b || typeof b !== 'object' || b.bridgeVersion !== DESKTOP_BRIDGE_VERSION) return null;
  const fns: unknown[] = [
    b.auth?.state, b.auth?.signIn, b.auth?.cancel, b.auth?.signOut, b.auth?.onChange,
    b.notify, b.setRinging, b.setTrayState, b.setCallActivity, b.showWindow,
    b.devices?.microphoneAccess, b.devices?.askMicrophone, b.devices?.onPermissionDenied,
    b.settings?.get, b.settings?.set,
    b.onDialLink, b.onNotificationClicked, b.onPower, b.onTrayCommand,
  ];
  return fns.every(f => typeof f === 'function') ? (b as DesktopBridge) : null;
}

/** Errors cross IPC as `SIDETONE_DESKTOP_ERROR:{"code","message"}` (apps/desktop/src/main/ipc.ts); only the code is kept. */
export function desktopErrorCode(error: unknown): string {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const at = message.indexOf('SIDETONE_DESKTOP_ERROR:');
  if (at === -1) return 'desktop_error';
  try {
    const code = (JSON.parse(message.slice(at + 'SIDETONE_DESKTOP_ERROR:'.length)) as { code?: unknown }).code;
    return typeof code === 'string' && /^[a-z0-9_]{1,80}$/.test(code) ? code : 'desktop_error';
  } catch {
    return 'desktop_error';
  }
}

export const DesktopContext = createContext<DesktopBridge | null>(null);

export function useDesktop(): DesktopBridge | null {
  return useContext(DesktopContext);
}
